import React from 'react';
import { StandardTableActions, Select, ColoredBadge } from '../../components/ui';

import { Column } from '../../components/DataTable/DataTable';
import { TruncatedText } from '../../components/DataTable/TruncatedText';
import { CrmNote } from './useData';
import { FieldConfig } from '../../components/generic/fieldRenderers';
import { FormLayout } from '../../components/generic/FormLayoutRenderer';
import { formatDate as formatDateGlobal, APP_CONFIG } from '../../config/appConfig';

const NOTE_STATUS_COLOR_ACTIVE = '#22c55e';
const NOTE_STATUS_COLOR_ARCHIVED = '#f97316';

const renderNoteStatusBadge = (archived?: boolean, className?: string) => (
  <ColoredBadge
    label={archived === true ? 'Archived' : 'Active'}
    color={archived === true ? NOTE_STATUS_COLOR_ARCHIVED : NOTE_STATUS_COLOR_ACTIVE}
    className={className}
  />
);

// Module Metadata
export const moduleConfig = {
  // Module Information
  module: {
    title: 'Notes',
    description: 'Manage your notes database',
    permissionModuleKey: 'crm_notes',
    route: {
      list: '/crm_notes',
      new: '/crm_notes/new',
      detail: (id: number) => `/crm_notes/${id}`,
      edit: (id: number) => `/crm_notes/${id}/edit`,
    },
    // API Endpoints
    api: {
      list: '/crm-notes',
      detail: (id: number) => `/crm-notes/${id}`,
    },
  },

  // Pagination Settings
  pagination: {
    initialItemsPerPage: APP_CONFIG.table.defaultPageSize,
    itemsPerPageOptions: APP_CONFIG.table.pageSizeOptions,
  },

  // Search Settings
  search: {
    placeholder: 'Search notes by client, description, or users...',
  },

  // Filter Groups Configuration - Groups filters into tabs for better organization
  filterGroups: [
    {
      name: 'basic',
      label: 'Basic',
      icon: '🔍',
      fields: [
        'noteDescr', 'clientId', 'noteFrom', 'noteTo', 'noteArchived', 'noteCreatedAt', 'noteUpdatedAt'
      ],
    },
  ],

  // Filter Configuration
  filters: {
    fields: [
      {
        key: 'noteDescr',
        label: 'Description',
        placeholder: 'All Description',
        type: 'text' as const,
        getValue: (item: CrmNote) => item.noteDescr,
        options: [],
      },
      {
        key: 'clientId',
        label: 'Client',
        placeholder: 'All Clients',
        type: 'select' as const,
        getValue: (item: CrmNote) =>
          item.client?.clientName || (item.clientId ? `Client #${item.clientId}` : ''),
        options: [],
      },
      {
        key: 'noteFrom',
        label: 'From User',
        placeholder: 'All From Users',
        type: 'select' as const,
        getValue: (item: CrmNote) => {
          if (item.noteFromUser) {
            return `${item.noteFromUser.userFirstName || ''} ${item.noteFromUser.userLastName || ''}`.trim();
          }
          return item.noteFrom?.toString();
        },
        options: [],
      },
      {
        key: 'noteTo',
        label: 'To User',
        placeholder: 'All To Users',
        type: 'select' as const,
        getValue: (item: CrmNote) => {
          if (item.noteToUser) {
            return `${item.noteToUser.userFirstName || ''} ${item.noteToUser.userLastName || ''}`.trim();
          }
          return item.noteTo?.toString();
        },
        options: [],
      },
      {
        key: 'noteArchived',
        label: 'Status',
        placeholder: 'All Status',
        type: 'boolean' as const,
        getValue: (item: CrmNote) => item.noteArchived ? 'Archived' : 'Active',
        options: ['Active', 'Archived'],
      },
      {
        key: 'noteCreatedAt',
        label: 'Created At',
        placeholder: 'All Created At',
        type: 'date' as const,
        getValue: (item: CrmNote) =>
          item.noteCreatedAt
            ? formatDateGlobal(item.noteCreatedAt)
            : undefined,
        options: [],
      },
      {
        key: 'noteUpdatedAt',
        label: 'Updated At',
        placeholder: 'All Updated At',
        type: 'date' as const,
        getValue: (item: CrmNote) =>
          item.noteUpdatedAt
            ? formatDateGlobal(item.noteUpdatedAt)
            : undefined,
        options: [],
      },
    ],
    // Filter logic functions
    applyFilter: (
      items: CrmNote[],
      filters: {
        noteDescr: string[] | string;
        clientId: string[];
        noteFrom: string[];
        noteTo: string[];
        noteArchived: string[];
        noteCreatedAt: string[];
        noteUpdatedAt: string[];
      },
      dateRanges?: Record<string, { start: Date | null; end: Date | null }>
    ) => {
      const ranges = dateRanges || {};
      return items.filter((item) => {
        // Description filter
        const descrFilter = filters.noteDescr;
        if (typeof descrFilter === 'string' && descrFilter.trim() !== '') {
          const value = (item.noteDescr || '').toLowerCase();
          if (!value.includes(descrFilter.toLowerCase())) return false;
        } else if (Array.isArray(descrFilter) && descrFilter.length > 0) {
          const value = (item.noteDescr || '').toLowerCase();
          const matches = descrFilter.some((filter) => {
            const filterLower = filter.toLowerCase();
            return value.includes(filterLower) || filterLower.includes(value);
          });
          if (!matches) return false;
        }

        // Client filter (matches by display name or ID)
        if (filters.clientId && filters.clientId.length > 0) {
          const displayValue = item.client?.clientName || (item.clientId ? `Client #${item.clientId}` : '');
          const matches = filters.clientId.some((filter) => {
            return displayValue === filter ||
                   (item.clientId && filter === `Client #${item.clientId}`) ||
                   (item.client?.clientName && filter === item.client.clientName);
          });
          if (!matches) return false;
        }

        // From User filter
        if (filters.noteFrom && filters.noteFrom.length > 0) {
          const fromUserName = item.noteFromUser 
            ? `${item.noteFromUser.userFirstName || ''} ${item.noteFromUser.userLastName || ''}`.trim()
            : '';
          const fromUserId = item.noteFrom?.toString() || '';
          const matches = filters.noteFrom.some((filter) => {
            return fromUserName.toLowerCase().includes(filter.toLowerCase()) ||
              fromUserId === filter ||
              filter.toLowerCase().includes(fromUserName.toLowerCase());
          });
          if (!matches) return false;
        }

        // To User filter
        if (filters.noteTo && filters.noteTo.length > 0) {
          const toUserName = item.noteToUser 
            ? `${item.noteToUser.userFirstName || ''} ${item.noteToUser.userLastName || ''}`.trim()
            : '';
          const toUserId = item.noteTo?.toString() || '';
          const matches = filters.noteTo.some((filter) => {
            return toUserName.toLowerCase().includes(filter.toLowerCase()) ||
              toUserId === filter ||
              filter.toLowerCase().includes(toUserName.toLowerCase());
          });
          if (!matches) return false;
        }

        // Archived status filter
        if (filters.noteArchived && filters.noteArchived.length > 0) {
          const status = item.noteArchived ? 'Archived' : 'Active';
          if (!filters.noteArchived.includes(status)) return false;
        }

        // Created At filter
        if (filters.noteCreatedAt.length > 0) {
          if (ranges.noteCreatedAt) {
            const range = ranges.noteCreatedAt;
            if (range.start && range.end && item.noteCreatedAt) {
              const itemDate = new Date(item.noteCreatedAt);
              if (itemDate < range.start || itemDate > range.end) return false;
            }
          } else {
            const value = item.noteCreatedAt
              ? formatDateGlobal(item.noteCreatedAt)
              : '';
            const matches = filters.noteCreatedAt.some((filter) => {
              return value.includes(filter) || filter.includes(value);
            });
            if (!matches) return false;
          }
        }

        // Updated At filter
        if (filters.noteUpdatedAt.length > 0) {
          if (ranges.noteUpdatedAt) {
            const range = ranges.noteUpdatedAt;
            if (range.start && range.end && item.noteUpdatedAt) {
              const itemDate = new Date(item.noteUpdatedAt);
              if (itemDate < range.start || itemDate > range.end) return false;
            }
          } else {
            const value = item.noteUpdatedAt
              ? formatDateGlobal(item.noteUpdatedAt)
              : '';
            const matches = filters.noteUpdatedAt.some((filter) => {
              return value.includes(filter) || filter.includes(value);
            });
            if (!matches) return false;
          }
        }

        return true;
      });
    },
  },

  // Toolbar Actions Configuration
  toolbar: {
    actions: [
      {
        key: 'download',
        icon: 'download',
        title: 'Download',
        showTitle: false,
        variant: 'default' as const,
        dropdown: [
          {
            key: 'export-csv',
            label: 'Export to CSV',
            icon: 'download',
            onClick: async (exportData?: {
              sortedData: any[];
              tableColumns: any[];
              entityName: string;
              exportFileName: string;
              formatDate?: (dateString?: string) => string;
            }) => {
              if (!exportData) {
                console.error('Export data not provided');
                return;
              }
              try {
                const { exportToCSV } = await import('../../services/exportService');
                await exportToCSV(exportData);
              } catch (error: any) {
                console.error('Failed to export to CSV:', error);
                throw new Error(error?.message || 'Failed to export to CSV. Please check the console for details.');
              }
            },
          },
          {
            key: 'export-excel',
            label: 'Export to Excel',
            icon: 'download',
            onClick: async (exportData?: {
              sortedData: any[];
              tableColumns: any[];
              entityName: string;
              exportFileName: string;
              formatDate?: (dateString?: string) => string;
            }) => {
              if (!exportData) {
                console.error('Export data not provided');
                return;
              }
              try {
                const { exportToExcel } = await import('../../components/exports');
                await exportToExcel(exportData);
              } catch (error: any) {
                console.error('Failed to export to Excel:', error);
                throw new Error(error?.message || 'Failed to export to Excel. Please check the console for details.');
              }
            },
          },
          {
            key: 'export-pdf',
            label: 'Export to PDF',
            icon: 'download',
            onClick: async (exportData?: {
              sortedData: any[];
              tableColumns: any[];
              entityName: string;
              exportFileName: string;
              formatDate?: (dateString?: string) => string;
            }) => {
              if (!exportData) {
                console.error('Export data not provided');
                return;
              }
              try {
                const { exportToPDF } = await import('../../utils/exportToPdf');
                await exportToPDF(exportData);
              } catch (error: any) {
                console.error('Failed to export to PDF:', error);
                throw new Error(error?.message || 'Failed to export to PDF. Please check the console for details.');
              }
            },
          },
        ],
      },
      {
        key: 'add',
        icon: 'add',
        title: 'Add New Note',
        showTitle: false,
        variant: 'primary' as const,
        href: '/crm_notes/new',
      },
    ],
  },

  // Table Configuration
  table: {
    actionsPosition: 'left' as const,
    onRowClick: (item: CrmNote) => {
      window.location.href = `/crm_notes/${item.noteId}`;
    },
    keyExtractor: (item: CrmNote) => item.noteId,
    getClientId: (item: CrmNote) => item.clientId ?? null,
  },

  // Empty States
  emptyStates: {
    noData: 'Get started by creating a new note.',
    noSearchResults: 'No notes found matching your search criteria.',
    noFilterResults: 'No notes match the selected filters.',
  },

  // Delete Confirmation
  delete: {
    title: 'Delete Note',
    message: 'Are you sure you want to delete this note? This action cannot be undone.',
    confirmText: 'Delete',
    cancelText: 'Cancel',
    errorMessage: 'Failed to delete note. Please try again.',
    handler: async (id: number) => {
      const { deleteCrmNote } = await import('../../services/mutations');
      await deleteCrmNote(id);
    },
  },

  // Utility Functions
  utils: {
    formatDate: (dateString?: string) => {
      if (!dateString) return '-';
      return formatDateGlobal(dateString);
    },
  },

  // Card View Configuration
  cardView: {
    getId: (item: CrmNote) => item.noteId,
    getTitle: (item: CrmNote) => {
      const truncated = item.noteDescr 
        ? (item.noteDescr.length > 60 ? item.noteDescr.substring(0, 60) + '...' : item.noteDescr)
        : `Note #${item.noteId}`;
      return truncated;
    },
    getSubtitle: (item: CrmNote) => {
      if (item.client?.clientName) {
        return `Client: ${item.client.clientName}`;
      }
      return '';
    },
    getStatusBadge: (item: CrmNote) => ({
      label: item.noteArchived ? 'Archived' : 'Active',
      variant: (item.noteArchived ? 'warning' : 'accent') as 'default' | 'success' | 'warning' | 'danger' | 'info',
    }),
    getCardFields: (item: CrmNote) => {
      const fields: Array<{
        label?: string;
        value: React.ReactNode;
        icon?: React.ReactNode;
        badge?: boolean;
        badgeVariant?: 'default' | 'success' | 'warning' | 'danger' | 'info' | 'blue' | 'purple';
      }> = [];

      // Client
      if (item.client?.clientName) {
        fields.push({
          value: item.client.clientName,
          icon: (
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
            </svg>
          ),
        });
      }

      // From User
      if (item.noteFromUser) {
        const fromName = `${item.noteFromUser.userFirstName || ''} ${item.noteFromUser.userLastName || ''}`.trim();
        if (fromName) {
          fields.push({
            label: 'From',
            value: fromName,
            icon: (
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
              </svg>
            ),
          });
        }
      }

      // To User
      if (item.noteToUser) {
        const toName = `${item.noteToUser.userFirstName || ''} ${item.noteToUser.userLastName || ''}`.trim();
        if (toName) {
          fields.push({
            label: 'To',
            value: toName,
            icon: (
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
              </svg>
            ),
          });
        }
      }

      // Created Date
      if (item.noteCreatedAt) {
        fields.push({
          label: 'Created',
          value: formatDateGlobal(item.noteCreatedAt),
          icon: (
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
            </svg>
          ),
        });
      }

      // Updated Date
      if (item.noteUpdatedAt) {
        fields.push({
          label: 'Updated',
          value: formatDateGlobal(item.noteUpdatedAt),
          icon: (
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
            </svg>
          ),
        });
      }

      return fields;
    },
    statusFilter: {
      enabled: true,
      defaultFilter: 'all',
      options: [
        { value: 'all', label: 'All Notes' },
        { value: 'active', label: 'Active' },
        { value: 'archived', label: 'Archived' },
      ],
      getStatus: (item: CrmNote) => (item.noteArchived ? 'archived' : 'active'),
    },
    updateStatus: async (id: number, newStatus: any) => {
      const { updateCrmNote } = await import('../../services/mutations');
      await updateCrmNote(id, { noteArchived: newStatus === 'archived' });
    },
    renderStatusSelector: (item: CrmNote, isUpdating: boolean, onStatusChange: (newStatus: any) => void) => {
      const noteStatusOptions = [
        { value: 'active', label: 'Active' },
        { value: 'archived', label: 'Archived' },
      ];
      return (
        <Select
          value={item.noteArchived ? 'archived' : 'active'}
          onChange={(e: any) => onStatusChange(e.target.value)}
          disabled={isUpdating}
          className="text-xs"
          showClearButton={false}
          options={noteStatusOptions}
        />
      );
    },
    actions: {
      view: true,
      edit: true,
      delete: true,
    },
  },

  // Detail View Configuration
  detail: {
    subtitle: 'Note Details',
    notFoundTitle: 'Note not found',
    notFoundMessage: 'The note you\'re looking for doesn\'t exist.',
    notFoundBackText: 'Back to Notes',
    defaultTitle: 'Unnamed Note',
    getTitle: (item: CrmNote) => {
      return item.noteDescr || `Note #${item.noteId}`;
    },
  },

  // Form Configuration
  form: {
    new: {
      title: 'New Note',
      subtitle: 'Add a new note to your database',
      submitText: 'Create Note',
    },
    edit: {
      title: 'Edit Note',
      submitText: 'Update Note',
    },
    cancelText: 'Cancel',
    loadingText: 'Loading note...',
    errorMessage: 'Failed to save note. Please try again.',
    create: async (data: Partial<CrmNote>) => {
      const { createCrmNote } = await import('../../services/mutations');
      return await createCrmNote(data);
    },
    update: async (id: number, data: Partial<CrmNote>) => {
      const { updateCrmNote } = await import('../../services/mutations');
      return await updateCrmNote(id, data);
    },
  },

  // Unified Field Configuration
  fields: [
    {
      name: 'noteDescr',
      label: 'Description',
      type: 'textarea',
      required: true,
      placeholder: 'Enter note description',
      rows: 6,
      validation: {
        required: true,
        minLength: 3,
        maxLength: 2000,
      },
    },
    {
      name: 'clientId',
      label: 'Client',
      type: 'select',
      placeholder: 'Select a client (optional)',
      options: [], // Will be populated dynamically via API
      apiEndpoint: '/crm-clients', // API endpoint to fetch all client options
      labelField: 'clientName', // Field name in API response to use as label
    },
    {
      name: 'noteFrom',
      label: 'From',
      type: 'select',
      required: true,
      placeholder: 'Select a user',
      options: [], // Will be populated dynamically via API
      apiEndpoint: '/users', // API endpoint to fetch all user options
      labelField: 'userDisplayName', // Field name in API response to use as label
      validation: {
        required: true,
      },
    },
    {
      name: 'noteTo',
      label: 'To',
      type: 'select',
      required: true,
      placeholder: 'Select a user',
      options: [], // Will be populated dynamically via API
      apiEndpoint: '/users', // API endpoint to fetch all user options
      labelField: 'userDisplayName', // Field name in API response to use as label
      validation: {
        required: true,
      },
    },
    {
      name: 'noteArchived',
      label: 'Archived',
      type: 'switch',
      switchLabel: 'Mark as archived',
      description: 'Check if this note is archived',
      labelPosition: 'left',
      defaultValue: false,
    },
  ] as FieldConfig[],

  // Form Layout Configuration
  // Form Layout Configuration (crm-tasks style: gridCols + fieldSpans)
  formLayout: {
    mode: 'default',
    sections: [
      {
        id: 'basic',
        title: 'Note Information',
        icon: '📝',
        gridCols: 'md:grid-cols-12',
        fields: ['noteDescr', 'clientId', 'noteFrom', 'noteTo', 'noteArchived'],
        fieldSpans: {
          noteDescr: 'md:col-span-12',
          clientId: 'md:col-span-6',
          noteFrom: 'md:col-span-6',
          noteTo: 'md:col-span-6',
          noteArchived: 'md:col-span-6',
        },
        collapsible: false,
      },
    ],
  } as FormLayout,
};

// Helper function to get columns
export const getColumns = (formatDate: (dateString?: string) => string): Column<CrmNote>[] => [
  {
    key: 'noteDescr',
    header: 'Description',
    sortable: true,
    render: (value, row) => {
      const clientLine = row.client?.clientName || (row.clientId ? `Client #${row.clientId}` : null);
      if (!clientLine) {
        return (
          <TruncatedText
            text={value || ''}
            maxLength={50}
            showTooltip={true}
          />
        );
      }
      return (
        <div className="flex flex-col min-w-0">
          <TruncatedText
            text={value || ''}
            maxLength={50}
            showTooltip={true}
          />
          <span className="text-sm text-gray-500 dark:text-gray-400 truncate mt-0.5">{clientLine}</span>
        </div>
      );
    },
  },
  {
    key: 'noteFrom',
    header: 'From',
    sortable: true,
    render: (value, row) => {
      const u = row.noteFromUser;
      if (u) {
        const display =
          u.userDisplayName ||
          `${u.userFirstName || ''} ${u.userLastName || ''}`.trim() ||
          (row.noteFrom ? `User #${row.noteFrom}` : '');
        return display || '-';
      }
      return row.noteFrom ? `User #${row.noteFrom}` : '-';
    },
  },
  {
    key: 'noteTo',
    header: 'To',
    sortable: true,
    render: (value, row) => {
      const u = row.noteToUser;
      if (u) {
        const display =
          u.userDisplayName ||
          `${u.userFirstName || ''} ${u.userLastName || ''}`.trim() ||
          (row.noteTo ? `User #${row.noteTo}` : '');
        return display || '-';
      }
      return row.noteTo ? `User #${row.noteTo}` : '-';
    },
  },
  {
    key: 'noteArchived',
    header: 'Status',
    sortable: true,
    render: (_value, row) => renderNoteStatusBadge(row.noteArchived),
  },
  {
    key: 'noteCreatedAt',
    header: 'Created At',
    sortable: true,
    render: (value) => formatDate(value),
  },
  {
    key: 'noteUpdatedAt',
    header: 'Updated At',
    sortable: true,
    render: (value) => formatDate(value),
  },
];

// Helper function to get detail sections
export const getDetailSections = (
  item: CrmNote,
  formatDate: (dateString?: string) => string
) => {
  return [
    {
      title: 'Note Information',
      fields: [
        {
          label: 'Description',
          value: (
            <TruncatedText
              text={item.noteDescr || '-'}
              maxLength={50}
              showTooltip={true}
            />
          ),
        },
        {
          label: 'Client',
          value: item.client?.clientName || (item.clientId ? `Client #${item.clientId}` : 'None'),
        },
        {
          label: 'From',
          value: (() => {
            const u: any = item.noteFromUser;
            if (u) {
              const displayName = u.userDisplayName as string | undefined;
              const fullName = `${u.userFirstName || ''} ${u.userLastName || ''}`.trim();
              return displayName || fullName || (item.noteFrom ? `User #${item.noteFrom}` : 'None');
            }
            return item.noteFrom ? `User #${item.noteFrom}` : 'None';
          })(),
        },
        {
          label: 'To',
          value: (() => {
            const u: any = item.noteToUser;
            if (u) {
              const displayName = u.userDisplayName as string | undefined;
              const fullName = `${u.userFirstName || ''} ${u.userLastName || ''}`.trim();
              return displayName || fullName || (item.noteTo ? `User #${item.noteTo}` : 'None');
            }
            return item.noteTo ? `User #${item.noteTo}` : 'None';
          })(),
        },
        {
          label: 'Status',
          value: renderNoteStatusBadge(item.noteArchived),
        },
        {
          label: 'Created At',
          value: formatDate(item.noteCreatedAt),
          condition: !!item.noteCreatedAt,
        },
        {
          label: 'Updated At',
          value: formatDate(item.noteUpdatedAt),
          condition: !!item.noteUpdatedAt,
        },
        {
          label: 'Archived Date',
          value: formatDate(item.noteArchivedDate),
          condition: !!item.noteArchived && !!item.noteArchivedDate,
        },
      ],
    },
  ];
};

// Helper function to get status badges
export const getStatusBadges = (item: CrmNote) => {
  return renderNoteStatusBadge(item.noteArchived, 'px-3 py-1 text-sm font-semibold');
};

// Helper function to get form sections (legacy, use formLayout instead)
export const getFormSections = () => {
  return [];
};

// Helper function to render toolbar icon (same icon style for view toggle dropdown)
export const getToolbarIcon = (iconName: string): React.ReactNode => {
  const iconClass = 'w-5 h-5';
  const svgProps = { className: iconClass, fill: 'none' as const, stroke: 'currentColor' as const, viewBox: '0 0 24 24' };
  switch (iconName) {
    case 'settings':
      return (
        <svg {...svgProps}>
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
        </svg>
      );
    case 'download':
      return (
        <svg {...svgProps}>
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
        </svg>
      );
    case 'add':
      return (
        <svg {...svgProps}>
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
        </svg>
      );
    case 'list':
      return (
        <svg {...svgProps}>
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 10h16M4 14h16M4 18h16" />
        </svg>
      );
    case 'card':
      return (
        <svg {...svgProps}>
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
        </svg>
      );
    default:
      return null;
  }
};

// Helper function to render table action buttons
export const getTableActions = (
  item: CrmNote,
  onDelete: (id: number) => void,
  _colors?: unknown,
  _listContext?: unknown,
  options?: { canDelete?: boolean; canEdit?: boolean }
): React.ReactNode => {
  const canDelete = options?.canDelete !== false;
  const canEdit = options?.canEdit !== false;
  return (
    <StandardTableActions
      viewTo={moduleConfig.module.route.detail(item.noteId)}
      editTo={moduleConfig.module.route.edit(item.noteId)}
      onDelete={() => onDelete(item.noteId)}
      canEdit={canEdit}
      canDelete={canDelete}
    />
  );
};


export const getInitialFormData = (): Partial<CrmNote> => {
  return {
    noteDescr: '',
    clientId: undefined,
    noteFrom: undefined,
    noteTo: undefined,
    noteArchived: false,
  };
};

// Helper function to map item data to form data
export const mapItemToFormData = (item: CrmNote): Partial<CrmNote> => {
  return {
    noteDescr: item.noteDescr || '',
    clientId: item.clientId || undefined,
    noteFrom: item.noteFrom || undefined,
    noteTo: item.noteTo || undefined,
    noteArchived: item.noteArchived ?? false,
  };
};

export type ModuleConfig = typeof moduleConfig;

